if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
        }
        this.dotButtons = this.elements.viewer.querySelectorAll('.slider-counter__link');
        if (this.dotButtons?.length) {
          this.dotButtons.forEach((btn, index) => {
            btn.addEventListener('click', (event) => {
              event.preventDefault();
              const slides = this.elements.viewer.querySelectorAll('[id^="Slide-"]');
              const target = slides[index];
              if (target) {
                this.elements.viewer.slider.scrollTo({ left: target.offsetLeft });
                // Atualiza o dot ativo imediatamente ao clicar
                this.setActiveDot(index + 1);
              }
            });
          });
          this.setActiveDot(1);
          
          // Adiciona listener de scroll para atualizar dots ao arrastar
          const slider = this.elements.viewer.querySelector('[id^="Slider-"]');
          if (slider) {
            this.slider = slider;
            this.sliderItems = slider.querySelectorAll('[id^="Slide-"]');
            slider.addEventListener('scroll', debounce(this.onScroll.bind(this), 100));
          }
        }
        if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        this.setThumbnailsHeightBound = this.setThumbnailsHeight.bind(this);
        if (this.mql.matches) {
          this.setThumbnailsHeight();
          window.addEventListener('resize', this.setThumbnailsHeightBound);
        }
      }

      setThumbnailsHeight() {
        if (!this.elements.thumbnails || !this.mql.matches) return;

        const isVerticalThumbs =
          this.classList.contains('product__media-gallery--thumb-left') ||
          this.classList.contains('product__media-gallery--thumb-right');
        if (!isVerticalThumbs) return;

        const thumbnailsSlider = this.elements.thumbnails.querySelector('.thumbnail-list.slider--tablet-up');
        if (!thumbnailsSlider) return;

        const items = Array.from(thumbnailsSlider.querySelectorAll('.thumbnail-list__item.slider__slide'));
        if (!items.length) return;

        const firstRect = items[0].getBoundingClientRect();
        let step = firstRect.height;

        if (items.length > 1) {
          const secondRect = items[1].getBoundingClientRect();
          const diff = Math.abs(secondRect.top - firstRect.top);
          if (diff > 0) step = diff;
        }

        const visibleItems = 4;
        const totalHeight = step * visibleItems;
        thumbnailsSlider.style.maxHeight = `${totalHeight}px`;
      }

      onScroll() {
        if (!this.slider || !this.sliderItems.length || !this.dotButtons?.length) return;
        
        const sliderItemsArray = Array.from(this.sliderItems).filter((el) => el.clientWidth > 0);
        if (sliderItemsArray.length < 1) return;
        
        const sliderItemOffset = sliderItemsArray.length > 1 
          ? sliderItemsArray[1].offsetLeft - sliderItemsArray[0].offsetLeft 
          : sliderItemsArray[0].clientWidth;
        
        const currentPage = Math.round(this.slider.scrollLeft / sliderItemOffset) + 1;
        this.setActiveDot(currentPage);
      }

      onSlideChanged(event) {
        this.setActiveDot(event.detail.currentPage || 1);
        
        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
          );
          this.setActiveThumbnail(thumbnail);
        }
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        const isVerticalThumbs =
          this.classList.contains('product__media-gallery--thumb-left') ||
          this.classList.contains('product__media-gallery--thumb-right');

        this.elements.thumbnails.slider.scrollTo({
          left: isVerticalThumbs ? 0 : thumbnail.offsetLeft,
          top: isVerticalThumbs ? thumbnail.offsetTop : 0,
        });
      }

      setActiveDot(page) {
        if (!this.dotButtons?.length) return;
        this.dotButtons.forEach((btn) => {
          btn.classList.remove('slider-counter__link--active');
          btn.removeAttribute('aria-current');
        });
        const index = Math.max(1, Math.min(page, this.dotButtons.length)) - 1;
        this.dotButtons[index].classList.add('slider-counter__link--active');
        this.dotButtons[index].setAttribute('aria-current', 'true');
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
